import React, { useState, useEffect, useCallback, useRef } from 'react';
// Import searchFranchisees service
import { listFranchisees, deleteFranchisee, searchFranchisees, resetFranchiseePassword, updateFranchiseeStatus } from '../services/franchiseeService';
// Added FaSearch and FaTimes icons
import { FaPlus, FaEdit, FaTrashAlt, FaEnvelopeOpenText, FaSearch, FaTimes, FaKey, FaToggleOn, FaToggleOff, FaCopy, FaSpinner, FaClone } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import ConfirmationModal from '../components/ConfirmationModal';
import StatusChangeModal from '../components/StatusChangeModal';
import DuplicateFranchiseesModal from '../components/DuplicateFranchiseesModal';

const PAGE_SIZE = 20;

const FranchiseesPage = () => {
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true); // General loading for fetch operations
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  const [apiError, setApiError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [franchiseeToDelete, setFranchiseeToDelete] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [franchiseeToResetPassword, setFranchiseeToResetPassword] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false); // For "Searching..." text, tied to search term length
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [franchiseeToUpdateStatus, setFranchiseeToUpdateStatus] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // Track which franchisee is being updated
  const [isDuplicatesModalOpen, setIsDuplicatesModalOpen] = useState(false);

  // --- Filter and Copy Emails State ---
  const [filterType, setFilterType] = useState('all'); // 'all' | 'regional' | 'external'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'suspended'
  const [copyingEmails, setCopyingEmails] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Any filter active means we must fetch the complete list (see fetchAllFranchisees) instead of
  // filtering the current page, since a match can be scattered anywhere across pages.
  const isFilterActive = filterType !== 'all' || statusFilter !== 'all';
  const [allFranchiseesForFilter, setAllFranchiseesForFilter] = useState([]);
  const [isFilterModeLoading, setIsFilterModeLoading] = useState(false);
  const filterFetchIdRef = useRef(0); // guards against a stale full-fetch resolving after a newer one started

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [pageStartDocIdsHistory, setPageStartDocIdsHistory] = useState([null]);
  const [isPaginatingAction, setIsPaginatingAction] = useState(false); // True during pagination button click action

  // Unified data fetching function
  const fetchFranchiseesData = useCallback(async (term, pageDocIdForFetch) => {
    setLoading(true);
    setError(null);
    // setIsSearching is primarily for the text message, driven by term length
    setIsSearching(term.length >= 3);

    try {
      let apiData;
      if (term.length >= 3) {
        apiData = await searchFranchisees({
          searchString: term,
          pageSize: PAGE_SIZE,
          startAfterDocId: pageDocIdForFetch,
        });
      } else if (term.length === 0) {
        apiData = await listFranchisees(PAGE_SIZE, pageDocIdForFetch);
      } else {
        setLoading(false); // Set loading false before returning for short terms
        setIsSearching(false); // Explicitly set searching false
        return { newFranchisees: [], newNextPageDocId: null };
      }
      return {
        newFranchisees: apiData.franchisees || [],
        newNextPageDocId: apiData.nextPageDocId || null,
      };
    } catch (err) {
      setError(err.message || 'Failed to load franchisees.');
      return { newFranchisees: [], newNextPageDocId: null }; // Return empty on error
    } finally {
      setLoading(false); // Ensure loading is always reset
    }
  }, [listFranchisees, searchFranchisees]);

  // --- Fetch-all (used by Copy Emails and by any active filter) ---
  const fetchAllFranchisees = useCallback(async (term) => {
    const allResults = [];
    let cursorDocId = null;

    do {
      const result = term.length >= 3
        ? await searchFranchisees({
            searchString: term,
            pageSize: 100,
            startAfterDocId: cursorDocId
          })
        : await listFranchisees(100, cursorDocId);

      allResults.push(...result.franchisees);
      cursorDocId = result.nextPageDocId;
    } while (cursorDocId);

    return allResults;
  }, []);

  // Debounced search handler
  const debouncedSearchHandler = useCallback(
    debounce(async (term) => {
      setCurrentPage(1);
      setPageStartDocIdsHistory([null]);
      // fetchFranchiseesData is async and returns data
      const result = await fetchFranchiseesData(term, null);
      setFranchisees(result.newFranchisees);
      setNextPageDocId(result.newNextPageDocId);
    }, 500),
    [fetchFranchiseesData, setCurrentPage, setPageStartDocIdsHistory, setFranchisees, setNextPageDocId] // Added state setters to dependencies
  );

  useEffect(() => {
    // Skip the paginated fetch while a filter is active — the full-list effect below supplies data instead.
    if (isFilterActive) return;
    // Clear stale page data so the loading/empty guards below don't show it while the refetch is in flight.
    setFranchisees([]);
    debouncedSearchHandler(searchTerm);
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [searchTerm, debouncedSearchHandler, isFilterActive]);

  // When any filter is active, fetch the COMPLETE list (all pages) and filter client-side —
  // a page-local filter would show an empty result for rare statuses like "Suspended" (5 of 237).
  const debouncedFullFetchHandler = useCallback(
    debounce(async (term) => {
      const requestId = ++filterFetchIdRef.current;
      setIsFilterModeLoading(true);
      setError(null);
      try {
        const all = await fetchAllFranchisees(term);
        if (requestId === filterFetchIdRef.current) {
          setAllFranchiseesForFilter(all);
        }
      } catch (err) {
        if (requestId === filterFetchIdRef.current) {
          setError(err.message || 'Failed to load franchisees.');
        }
      } finally {
        if (requestId === filterFetchIdRef.current) {
          setIsFilterModeLoading(false);
        }
      }
    }, 500),
    [fetchAllFranchisees]
  );

  useEffect(() => {
    if (!isFilterActive) return;
    debouncedFullFetchHandler(searchTerm);
    return () => {
      debouncedFullFetchHandler.cancel();
    };
  }, [searchTerm, isFilterActive, debouncedFullFetchHandler]);

  useEffect(() => {
    let timer;
    if (successMessage || apiError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setApiError(null);
        if (successMessage && location.state?.message) {
            navigate(location.pathname, { replace: true, state: {} });
        }
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, apiError, navigate, location.pathname, location.state?.message]);

  const handleAddFranchisee = () => navigate('/franchisees/add');
  const handleEdit = (id) => navigate(`/franchisees/edit/${id}`);

  const handleDelete = (franchisee) => {
    setFranchiseeToDelete(franchisee);
    setIsModalOpen(true);
    setApiError(null);
  };

  const handleResetPassword = (franchisee) => {
    setFranchiseeToResetPassword(franchisee);
    setIsResetPasswordModalOpen(true);
    setApiError(null);
  };

  const handleStatusToggle = (franchisee) => {
    // Normalize isActive: treat undefined or true as active (true), only false is inactive
    const currentStatus = franchisee.isActive !== false;
    const newStatus = !currentStatus;
    setFranchiseeToUpdateStatus(franchisee);
    setPendingStatusChange(newStatus);
    setIsStatusModalOpen(true);
    setApiError(null);
  };

  const confirmStatusChange = async () => {
    if (!franchiseeToUpdateStatus || pendingStatusChange === null) return;

    const newStatus = pendingStatusChange;
    setStatusUpdateLoading(franchiseeToUpdateStatus.id);

    try {
      await updateFranchiseeStatus(franchiseeToUpdateStatus.id, newStatus);

      // Update the franchisee in the local state
      setFranchisees(prevFranchisees =>
        prevFranchisees.map(fse =>
          fse.id === franchiseeToUpdateStatus.id
            ? { ...fse, isActive: newStatus }
            : fse
        )
      );

      setSuccessMessage(`Franchisee ${newStatus ? 'activated' : 'suspended'} successfully.`);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);

      // Close modal
      setIsStatusModalOpen(false);
      setFranchiseeToUpdateStatus(null);
      setPendingStatusChange(null);

    } catch (error) {
      console.error('Error updating franchisee status:', error);
      setApiError(error.message || 'Failed to update franchisee status.');

      // Clear error message after 5 seconds
      setTimeout(() => setApiError(null), 5000);
    } finally {
      setStatusUpdateLoading(null);
    }
  };

  const cancelStatusChange = () => {
    setIsStatusModalOpen(false);
    setFranchiseeToUpdateStatus(null);
    setPendingStatusChange(null);
    setApiError(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFranchiseeToDelete(null);
  };

  const closeResetPasswordModal = () => {
    setIsResetPasswordModalOpen(false);
    setFranchiseeToResetPassword(null);
  };

  // confirmDelete - MODIFIED
  const confirmDelete = async () => {
    if (!franchiseeToDelete) return;
    try {
      await deleteFranchisee(franchiseeToDelete.id);
      closeModal();
      setSuccessMessage(`Franchisee '${franchiseeToDelete.franchiseName || franchiseeToDelete.fseCode}' deleted successfully.`);
      const docIdForCurrentPageRefresh = pageStartDocIdsHistory[currentPage - 1];
      
      const result = await fetchFranchiseesData(searchTerm, docIdForCurrentPageRefresh);
      setFranchisees(result.newFranchisees);
      setNextPageDocId(result.newNextPageDocId);
      // If the current page becomes empty and it's not the first page, user can navigate back.
      // Or, implement auto-navigate to previous page if desired (more complex UX).
    } catch (err) {
      setApiError(err.message || 'Failed to delete franchisee.');
      closeModal();
    }
  };

  // Called after a duplicate-franchisee merge resolves; re-fetch the current page.
  const handleDuplicatesResolved = async () => {
    setSuccessMessage('Duplicate franchisee records merged successfully.');
    try {
      const docIdForCurrentPageRefresh = pageStartDocIdsHistory[currentPage - 1];
      const result = await fetchFranchiseesData(searchTerm, docIdForCurrentPageRefresh);
      setFranchisees(result.newFranchisees);
      setNextPageDocId(result.newNextPageDocId);
    } catch (err) {
      setApiError(err.message || 'Failed to refresh franchisees after merge.');
    }
  };

  // confirmResetPassword - UPDATED
  const confirmResetPassword = async () => {
    if (!franchiseeToResetPassword) return;
    setIsResettingPassword(true);
    try {
      await resetFranchiseePassword(franchiseeToResetPassword.email);
      setSuccessMessage(`Password reset successfully for '${franchiseeToResetPassword.franchiseName || franchiseeToResetPassword.fseCode}'. The franchisee will receive an email with their temporary password.`);
    } catch (err) {
      setApiError(err.message || 'Failed to reset password.');
    } finally {
      setIsResettingPassword(false);
      closeResetPasswordModal();
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // --- Pagination Handlers --- MODIFIED
  const handleNextPage = useCallback(async () => {
    if (!nextPageDocId || loading) {
      return;
    }
    setIsPaginatingAction(true);
    const docIdForNextFetch = nextPageDocId;

    try {
      const result = await fetchFranchiseesData(searchTerm, docIdForNextFetch);

      // Ensure result and newFranchisees are valid before proceeding
      if (result && result.newFranchisees && result.newFranchisees.length > 0) {
        setFranchisees(result.newFranchisees);
        setNextPageDocId(result.newNextPageDocId); // This could be null if the new page is the last one
        setCurrentPage(prevPage => prevPage + 1);
        setPageStartDocIdsHistory(prevHistory => [...prevHistory, docIdForNextFetch]);
      } else {
        // Next page was empty or result was invalid. Do not change franchisees list.
        // Mark that there's no further next page from this point.
        setNextPageDocId(null);
        // Current page, franchisees list, and history remain unchanged.
      }
    } catch (error) {
      // Error state is set by fetchFranchiseesData. Log if needed or handle specific UI.
      console.error("Error during pagination fetch:", error);
      setNextPageDocId(null); // Ensure next is disabled on error too
    } finally {
      setIsPaginatingAction(false);
    }
  }, [nextPageDocId, loading, searchTerm, fetchFranchiseesData, setCurrentPage, setPageStartDocIdsHistory, setFranchisees, setNextPageDocId]);

  const handlePrevPage = useCallback(async () => {
    if (currentPage <= 1 || loading) {
      return;
    }
    setIsPaginatingAction(true);
    const targetPage = currentPage - 1;
    const docIdForPrevFetch = pageStartDocIdsHistory[targetPage - 1];

    try {
      const result = await fetchFranchiseesData(searchTerm, docIdForPrevFetch);
      // Even if result.newFranchisees is empty (shouldn't happen for a valid prev page),
      // we update, as the user explicitly navigated back.
      setFranchisees(result.newFranchisees || []); 
      setNextPageDocId(result.newNextPageDocId); 
      setCurrentPage(targetPage);
      setPageStartDocIdsHistory(prevHistory => prevHistory.slice(0, targetPage));
    } catch (error) {
      console.error("Error during previous page fetch:", error);
      // Potentially revert optimistic state updates or show error to user
    } finally {
      setIsPaginatingAction(false);
    }
  }, [currentPage, loading, pageStartDocIdsHistory, searchTerm, fetchFranchiseesData, setCurrentPage, setPageStartDocIdsHistory, setFranchisees, setNextPageDocId]);

  // --- Filtering Helper ---
  // suspended = isActive === false; active = isActive !== false (85% of docs have no isActive field at all — treat as active)
  const getFilteredFranchisees = useCallback((franchiseesList) => {
    return franchiseesList.filter(fse => {
      const matchesType =
        filterType === 'external' ? fse.isExternal === true :
        filterType === 'regional' ? fse.isExternal !== true : // Includes undefined and false
        true;
      const matchesStatus =
        statusFilter === 'active' ? fse.isActive !== false :
        statusFilter === 'suspended' ? fse.isActive === false :
        true;
      return matchesType && matchesStatus;
    });
  }, [filterType, statusFilter]);

  const handleCopyEmails = async () => {
    setCopyingEmails(true);
    setApiError(null);

    try {
      // Fetch ALL franchisees (not just current page)
      const allFranchisees = await fetchAllFranchisees(searchTerm);

      // Apply filter
      const filteredFranchisees = getFilteredFranchisees(allFranchisees);

      // Suspended franchisees are excluded unless explicitly filtered for, so the default
      // copy stays safe to paste into a broadcast.
      const includeSuspended = statusFilter === 'suspended';
      const emails = filteredFranchisees
        .filter(fse => fse.email && (includeSuspended || fse.isActive !== false))
        .map(fse => fse.email);

      const emailList = emails.join(', ');

      // Copy to clipboard
      await navigator.clipboard.writeText(emailList);

      // Show success feedback
      setCopySuccess(true);
      setSuccessMessage(
        includeSuspended
          ? `Copied ${emails.length} suspended franchisee email addresses to clipboard`
          : `Copied ${emails.length} email addresses to clipboard (excluded suspended)`
      );
      setTimeout(() => {
        setCopySuccess(false);
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
      setApiError('Failed to copy emails to clipboard');
    } finally {
      setCopyingEmails(false);
    }
  };

  // Apply filter to displayed franchisees. While a filter is active, filter over the fully-loaded
  // list instead of the current page (a page-local filter would miss matches on other pages).
  const listSource = isFilterActive ? allFranchiseesForFilter : franchisees;
  const displayedFranchisees = getFilteredFranchisees(listSource);
  const listLoading = isFilterActive ? isFilterModeLoading : loading;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Title Row */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-bold text-jims-cyan-dark">Franchisees</h1>
      </div>

      {/* Buttons and Search Row */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
                <button
                    onClick={handleAddFranchisee}
                    className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
                >
                    <FaPlus className="mr-2" />
                    Add Franchisee
                </button>
                <Link
                    to="/franchisees/allowed-emails" // Link to the new page
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
                >
                    <FaEnvelopeOpenText className="mr-2" />
                    Manage Franchisee Emails
                </Link>

                {/* Copy Emails Button */}
                <button
                    onClick={handleCopyEmails}
                    disabled={copyingEmails}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Copy all filtered emails to clipboard"
                >
                    {copyingEmails ? (
                        <>
                            <FaSpinner className="mr-2 animate-spin" />
                            Copying...
                        </>
                    ) : copySuccess ? (
                        <>
                            <FaCopy className="mr-2" />
                            Copied!
                        </>
                    ) : (
                        <>
                            <FaCopy className="mr-2" />
                            Copy Emails
                        </>
                    )}
                </button>

                {/* View Duplicate Franchisees Button */}
                <button
                    onClick={() => setIsDuplicatesModalOpen(true)}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
                    title="Review and merge duplicate franchisee records"
                >
                    <FaClone className="mr-2" />
                    View Duplicate Franchisees
                </button>

                {/* Filter Dropdown */}
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="border border-gray-300 rounded-md py-2 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan"
                >
                    <option value="all">All Franchisees</option>
                    <option value="regional">Regional</option>
                    <option value="external">External</option>
                </select>

                {/* Status Filter Dropdown */}
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-md py-2 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan"
                >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                </select>
            </div>

            {/* Search Input */}
            <div className="relative flex-grow sm:flex-grow-0 sm:w-64 md:w-72">
                <input
                    type="text"
                    placeholder="Search by franchise name, first/last name or code..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="border border-gray-300 rounded-md py-2 px-4 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-transparent"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {searchTerm && (
                    <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        aria-label="Clear search"
                    >
                        <FaTimes />
                    </button>
                )}
            </div>
       </div>

      {/* Display Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}
      {/* Display API Error Message */}
      {apiError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {apiError}
        </div>
      )}

      {/* Loading and Error States */}
      {isFilterActive && listLoading && allFranchiseesForFilter.length === 0 && (
        <p className="text-center text-gray-500 py-4">
          Loading all franchisees to apply filters (fetches everything, ~3 requests)...
        </p>
      )}
      {!isFilterActive && (loading || isSearching) && !isPaginatingAction && franchisees.length === 0 && (
        <p className="text-center text-gray-500 py-4">
          {isSearching ? 'Searching...' : 'Loading franchisees...'}
        </p>
      )}
      {error && !apiError && (
        <p className="text-center text-red-500 bg-red-100 p-3 rounded mb-4">Error: {error}</p>
      )}
      {!isFilterActive && !loading && !isSearching && !isPaginatingAction && searchTerm.length > 0 && searchTerm.length < 3 && (
          <p className="text-center text-gray-500 p-6">Please enter at least 3 characters to search.</p>
      )}

      {/* Franchisees Table and Pagination Container */}
      {/* Show if not error and search term is valid (or no search term), and either not loading OR paginating */}
      {((!listLoading || isPaginatingAction)) && !error && !(searchTerm.length > 0 && searchTerm.length < 3) && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {displayedFranchisees.length === 0 ? (
            // Only show "No franchisees found" if not loading (or if paginating and result is empty)
            // If listLoading && !isPaginatingAction && displayedFranchisees.length === 0, the global loader above handles it.
            (!listLoading && !isPaginatingAction) && (
                <p className="text-center text-gray-500 p-6">
                    {isFilterActive ? 'No franchisees match the selected filters.' :
                    searchTerm && !(searchTerm.length > 0 && searchTerm.length < 3) ? 'No franchisees found matching your search.' :
                    (searchTerm.length === 0 ? 'No franchisees found.' : 'Please enter at least 3 characters to search.')}
                </p>
            )
          ) : (
            <table className="min-w-full leading-normal">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                  {/* Added Code Column */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Code</th>
                  {/* Added Franchise Column */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Franchise</th>
                  {/* Name column now only for contact person */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Name</th>
                  {/* Removed Territory Column */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Email</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">Phone</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Status</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedFranchisees.map((fse) => (
                  <tr key={fse.id} className="hover:bg-gray-50">
                    {/* Added Code Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.fseCode || 'N/A'}</p>
                    </td>
                    {/* Added Franchise Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.franchiseName || 'N/A'}</p>
                    </td>
                    {/* Modified Name Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{`${fse.firstName || ''} ${fse.lastName || ''}`.trim() || 'N/A'}</p>
                    </td>
                    {/* Removed Territory Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.email || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.phoneNumber || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                      <div className="flex items-center justify-center">
                        {fse.isActive !== false ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Suspended
                          </span>
                        )}
                        <button
                          onClick={() => handleStatusToggle(fse)}
                          disabled={statusUpdateLoading === fse.id}
                          className={`ml-2 transition duration-300 ${
                            statusUpdateLoading === fse.id
                              ? 'text-gray-400 cursor-not-allowed'
                              : fse.isActive !== false
                                ? 'text-green-500 hover:text-green-700'
                                : 'text-red-500 hover:text-red-700'
                          }`}
                          title={fse.isActive !== false ? 'Suspend Franchisee' : 'Activate Franchisee'}
                        >
                          {statusUpdateLoading === fse.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                          ) : fse.isActive !== false ? (
                            <FaToggleOn />
                          ) : (
                            <FaToggleOff />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                      <button onClick={() => handleEdit(fse.id)} className="text-blue-500 hover:text-blue-700 mr-3 transition duration-300" title="Edit Franchisee">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleResetPassword(fse)} className="text-yellow-500 hover:text-yellow-700 mr-3 transition duration-300" title="Reset Password">
                        <FaKey />
                      </button>
                      {/* Pass the whole franchisee object to handleDelete */}
                      <button onClick={() => handleDelete(fse)} className="text-red-500 hover:text-red-700 transition duration-300" title="Delete Franchisee">
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Filter mode loads the whole matching set at once, so there's nothing to page through —
              show a count instead of Prev/Next controls that would otherwise page a static array. */}
          {isFilterActive ? (
            displayedFranchisees.length > 0 && (
              <div className="mt-6 text-center text-sm text-gray-500 py-4">
                Showing all {displayedFranchisees.length} matching franchisee{displayedFranchisees.length === 1 ? '' : 's'}
              </div>
            )
          ) : (
            /* Pagination Controls - Show if there are franchisees or a next page is available */
            (franchisees.length > 0 || nextPageDocId) && (
              <div className="mt-6 flex justify-center items-center space-x-2 py-4">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1 || loading || isSearching} // `loading` will disable during fetch
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                >
                  {isPaginatingAction && loading ? 'Loading...' : 'Previous'}
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={!nextPageDocId || loading || isSearching} // `loading` will disable during fetch
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                >
                  {isPaginatingAction && loading ? 'Loading...' : 'Next'}
                </button>
              </div>
            )
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the franchisee \"${franchiseeToDelete?.franchiseName || franchiseeToDelete?.fseCode || ''}\"? This action cannot be undone.`}
      />

      {/* Reset Password Confirmation Modal */}
      <ConfirmationModal
        isOpen={isResetPasswordModalOpen}
        onClose={closeResetPasswordModal}
        onConfirm={confirmResetPassword}
        title="Confirm Password Reset"
        message={`Are you sure you want to reset the password for \"${franchiseeToResetPassword?.franchiseName || franchiseeToResetPassword?.fseCode || ''}\"? A temporary password will be sent to their email address (${franchiseeToResetPassword?.email || ''}) and they will be required to change it on their next login.`}
        isLoading={isResettingPassword}
        loadingText="Resetting password..."
      />

      {/* Status Change Confirmation Modal */}
      <StatusChangeModal
        isOpen={isStatusModalOpen}
        onClose={cancelStatusChange}
        onConfirm={confirmStatusChange}
        franchisee={franchiseeToUpdateStatus}
        newStatus={pendingStatusChange}
        isLoading={statusUpdateLoading === franchiseeToUpdateStatus?.id}
      />

      {/* Duplicate Franchisees Modal */}
      <DuplicateFranchiseesModal
        isOpen={isDuplicatesModalOpen}
        onClose={() => setIsDuplicatesModalOpen(false)}
        onResolved={handleDuplicatesResolved}
      />
    </div>
  );
};

export default FranchiseesPage;
